import { Header } from '@/components/header'
import { Logo } from '@/components/icons/logo'
import { DesktopNavbar } from '@/components/navbar/desktop-navbar'
import { MobileNavbar } from '@/components/navbar/mobile-navbar'
import { useElementsOverlap } from '@/hooks/elements-overlap'
import { cn } from '@/lib/utils'
import { type FC, type ReactNode, useCallback, useState } from 'react'

interface LayoutProps {
    children: ReactNode
}

export const Layout: FC<LayoutProps> = ({ children }) => {
    const [headerElement, setHeaderElement] = useState<HTMLDivElement | null>(null)
    const [contentElement, setContentElement] = useState<HTMLElement | null>(null)

    const headerRef = useCallback((node: HTMLDivElement | null) => {
        setHeaderElement(node)
    }, [])

    const contentRef = useCallback((node: HTMLElement | null) => {
        setContentElement(node)
    }, [])

    const { isOverlapping: contentScrolledPastHeader } = useElementsOverlap(headerElement, contentElement)

    return (
        <div className="flex h-full w-full p-0 gap-4 flex-col xl:flex-row lg:p-4">
            <aside className="grow shrink-0 xl:block hidden h-[calc(100%-2rem)] bg-background fixed min-w-[18.75rem] top-4 bottom-4">
                <DesktopNavbar />
            </aside>
            <div
                className={cn(
                    'xl:hidden flex flex-row gap-4 items-center fixed top-0 max-h-[4.75rem] z-10 w-full p-4 justify-between',
                    'transition-all',
                    contentScrolledPastHeader && 'glass-effect',
                )}
                ref={headerRef}
            >
                <div className="flex flex-row gap-4 items-center">
                    <MobileNavbar />
                    <Logo />
                </div>
                <Header />
            </div>
            <main
                className="flex-1 shrink-0 flex flex-col lg:ml-[18.75rem] relative h-full mt-[4.75rem] lg:mt-[2.75rem] lg:top-0 lg:p-0 px-4"
                ref={contentRef}
            >
                <Header className="hidden xl:flex shrink-0 grow-0 md:px-6 lg:fixed lg:z-10 max-h-[2.75rem] lg:top-4 lg:right-4" />
                <div className="container md:p-6 flex-1 max-w-6xl lg:absolute lg:top-[2.75rem]">{children}</div>
            </main>
        </div>
    )
}
